import {
  BadgeCheck,
  Leaf,
  Lightbulb,
  ShieldCheck,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { FaGlobeAfrica } from "react-icons/fa";
import { FaUsersGear } from "react-icons/fa6";
import { FiAward, FiBox, FiLayers, FiZap } from "react-icons/fi";
import { MdOutlineVerifiedUser } from "react-icons/md";

export interface NavLink {
  to: string;
  label_en: string;
  label_ar: string;
  key?: "capabilities" | "clients" | "careers";
  children?: { to: string; label_en: string; label_ar: string }[];
}

export const navLinks: NavLink[] = [
  {
    to: "/",
    label_en: "Home",
    label_ar: "الرئيسية",
  },

  {
    to: "/about",
    label_en: "About Us",
    label_ar: "من نحن",
  },

  {
    to: "/products",
    label_en: "Products",
    label_ar: "المنتجات",
    // children: [
    //   { to: "/products", label_en: "All Products", label_ar: "كل المنتجات" },
    //   {
    //     to: "/colors-finishes",
    //     label_en: "Colors & Finishes",
    //     label_ar: "الألوان والتشطيبات",
    //   },
    //   { to: "/factories", label_en: "Factories", label_ar: "المصانع" },
    // ],
  },

  {
    to: "/services",
    label_en: "Services",
    label_ar: "الخدمات",
  },

  {
    to: "/certifications",
    label_en: "Certifications",
    label_ar: "الشهادات",
  },

  // Toggleable pages — shown/hidden from the dashboard (page-settings).
  // The `key` matches the flag returned by useGetPageSettings.
  {
    to: "/capabilities",
    label_en: "Our Capabilities",
    label_ar: "قدراتنا",
    key: "capabilities",
  },

  {
    to: "/clients",
    label_en: "Clients",
    label_ar: "العملاء",
    key: "clients",
  },

  {
    to: "/careers",
    label_en: "Careers",
    label_ar: "الوظائف",
    key: "careers",
  },
];

export const banner = [
  {
    id: 1,
    value: "/video/banner.mp4",
    // value: "/video/main.mp4",
    type: "video",
    title: "curtain",
    subtitle: "curtainDes",
    img: "/03.png",
  },
  {
    id: 5,
    value: "/consulting-engineering.webp",
    type: "image",
    title: "fromConceptToCompletion",
    subtitle: "fromConceptToCompletionDes",
    img: "/03.png",
    main: "main",
  },
  {
    id: 2,
    value: "/banner.jpg",
    type: "image",
    title: "builtTitle",
    subtitle: "builtTitleDes",
    main: "secondary",
  },
  {
    id: 3,
    value: "/banner2.jpeg",
    type: "image",
    title: "reliable",
    subtitle: "reliableDes",
    titleAr: "الكمال الصناعي",
    subtitleAr: "خطوط حديثة، تشطيب فاخر",
    main: "trusted",
  },
  {
    id: 4,
    value: "/banner3.jpeg",
    type: "image",
    title: "trade",
    subtitle: "tradeDes",
    titleAr: "تجارة عالمية",
    subtitleAr: "جودة نُصدرها للعالم",
  },
];

export const excellencePoints = [
  {
    id: 1,
    value: 20,
    suffix: "+",
    label_en: "Countries",
    label_ar: "دول",
    icon: FaGlobeAfrica,
  },
  {
    id: 2,
    value: 10000,
    suffix: "+",
    label_en: "Clients",
    label_ar: "عملاء",
    icon: FaUsersGear,
  },
  {
    id: 3,
    value: 30,
    suffix: "+",
    label_en: "Years of Experience",
    label_ar: "سنوات من الخبرة",
    icon: FiAward,
  },
  {
    id: 4,
    value: 100,
    suffix: "%",
    label_en: "Quality",
    label_ar: "جودة",
    icon: MdOutlineVerifiedUser,
  },
];

export const mainFeatures = [
  {
    id: 1,
    name_ar: "معتمد ISO 9001",
    name_en: "ISO 9001 Certified",
  },
  {
    id: 2,
    name_ar: "خطوط إنتاج متقدمة",
    name_en: "Advanced Production Lines",
  },
  {
    id: 3,
    name_ar: "شبكة تصدير عالمية",
    name_en: "Global Export Network",
  },
  {
    id: 4,
    name_ar: "25+ سنة خبرة",
    name_en: "25+ Years of Experience",
  },
];

export const facilities = [
  {
    id: 1,
    name_en: "Extrusion Presses",
    name_ar: "ماكينات البثق (Extrusion Presses)",
    description_en:
      "High-capacity aluminum extrusion presses for producing custom profiles with tight tolerances.",
    description_ar:
      "ماكينات بثق عالية السعة لإنتاج مقاطع ألومنيوم مخصصة بدقة أبعاد عالية.",
    features: [
      "Multiple press sizes up to 10,000 tons",
      "Custom die tooling",
      "High repeatability",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 2,
    name_en: "Powder Coating Lines",
    name_ar: "خطوط الطلاء بالبودرة",
    description_en:
      "Automated powder coating lines offering durable and uniform finishes in a wide range of colors.",
    description_ar:
      "خطوط طلاء بودرة آلية توفر تشطيبات متينة ومتناسقة بمجموعة واسعة من الألوان.",
    features: [
      "Continuous ovens",
      "Electrostatic spray booths",
      "Thickness control and color matching",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 3,
    name_en: "Anodizing Plant",
    name_ar: "محطة الأنودة (Anodizing)",
    description_en:
      "Surface anodizing treatment to increase corrosion resistance and provide aesthetic metallic finishes.",
    description_ar:
      "معالجة الأنودة لزيادة مقاومة التآكل وتوفير تشطيبات معدنية جمالية.",
    features: [
      "Type II and Type III anodizing",
      "Color dyeing options",
      "Salt spray testing",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 4,
    name_en: "Wood Effect Finishing",
    name_ar: "تشطيبات مؤثرة بالخشب (Wood Effect)",
    description_en:
      "Advanced wood-grain finishing lines offering realistic wood patterns with high durability.",
    description_ar:
      "خطوط تشطيب تؤدي لمظهر حبيبات الخشب الواقعي مع متانة عالية.",
    features: [
      "Multiple wood grain patterns",
      "UV-resistant coatings",
      "Custom color matching",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 5,
    name_en: "CNC Machining & Fabrication",
    name_ar: "تشكيل وقطع CNC",
    description_en:
      "CNC milling, drilling and machining centers for precise component fabrication and custom parts.",
    description_ar:
      "مراكز التشكيل والقطع CNC للطحن والحفر وتصنيع قطع دقيقة ومخصصة.",
    features: [
      "Multi-axis CNC machines",
      "Automatic tool changers",
      "High precision tolerances",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 6,
    name_en: "Robotic Cutting & Welding",
    name_ar: "القص واللحام الروبوتي",
    description_en:
      "Robotic stations for automated cutting, welding and assembly to ensure speed and repeatability.",
    description_ar:
      "محطات روبوتية للقص واللحام والتجميع الآلي لضمان السرعة والتكرارية.",
    features: [
      "Laser & plasma cutting options",
      "Robotic welding arms",
      "Integrated quality checks",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 7,
    name_en: "Thermal Break Assembly",
    name_ar: "تجميع فواصل حرارية (Thermal Break)",
    description_en:
      "Dedicated lines for inserting thermal break materials to enhance insulation and energy performance.",
    description_ar:
      "خطوط مخصصة لإدخال مواد الفواصل الحرارية لتحسين العزل وكفاءة الطاقة.",
    features: [
      "Precision insertion machines",
      "Heat-sealing and bonding",
      "Thermal performance testing",
    ],
    image: "/facilities1.jpeg",
  },
  {
    id: 8,
    name_en: "Quality Control & Testing Lab",
    name_ar: "مختبر مراقبة الجودة والاختبارات",
    description_en:
      "In-house lab for mechanical, thermal and weathering tests (air, water, wind, salt spray, etc.).",
    description_ar:
      "مختبر داخلي للاختبارات الميكانيكية والحرارية وتجارب التحمل (هواء، ماء، يقاوم الرياح، رش ملحي، إلخ).",
    features: [
      "Air & water penetration tests",
      "Wind load testing",
      "Salt spray and accelerated aging",
    ],
    image: "/facilities1.jpeg",
  },
];

export const certificates = [
  {
    id: 1,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 2,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 3,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 4,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 5,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 6,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 7,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 8,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
  {
    id: 9,
    image: "/certificate.png",
    title_en: "Certificate of Excellence",
    title_ar: "شهادة التميز",
    description_en:
      "Recognized for outstanding quality standards and advanced aluminum architectural solutions.",
    description_ar:
      "معترف بها لمعايير الجودة الممتازة وحلول الألمنيوم المعمارية المتقدمة.",
  },
];

// ABOUT DATA
export const productionCapabilities = [
  {
    id: 1,
    title_en: "Standard RAL powder-coated finishes",
    title_ar: "تشطيبات مسحوق RAL القياسية",
    icon: FiBox,
  },
  {
    id: 2,
    title_en: "Super-durable & hyper-durable coatings (up to 35-year warranty)",
    title_ar: "طلاءات فائقة ومفرطة المتانة (حتى ضمان 35 سنة)",
    icon: FiLayers,
  },
  {
    id: 3,
    title_en: "High-quality wood-effect finishes (Qualideco certified)",
    title_ar: "تشطيبات تأثير الخشب عالية الجودة (معتمدة من Qualideco)",
    icon: FiAward,
  },
  {
    id: 4,
    title_en:
      "Thermal break crimped profiles using precision polyamide insertion technology",
    title_ar: "قطاعات بفاصل حراري مع تقنية إدخال البولي أميد الدقيقة",
    icon: FiZap,
  },
];

export const missionData = [
  {
    id: 1,
    name_en: "Consistent product quality",
    name_ar: "• جودة منتج متسقة",
  },
  {
    id: 2,
    name_en: "Customer-focused service",
    name_ar: "خدمة تركز على العميل",
  },
  {
    id: 3,
    name_en: "Responsible and efficient operations",
    name_ar: "عمليات مسؤولة وفعالة",
  },
  {
    id: 4,
    name_en: "Continuous improvement and investment in modern technologies",
    name_ar: "التحسين المستمر والاستثمار في التقنيات الحديثة",
  },
];

// VALUES
export const values = [
  {
    title_en: "Quality",
    title_ar: "الجودة",
    des_en:
      "We prioritize excellence in every product and service, ensuring consistency and performance at all stages of production.",
    des_ar:
      "نلتزم بالتميز في كل منتج وخدمة، مع ضمان الجودة والأداء في جميع مراحل الإنتاج.",
    icon: BadgeCheck, // جودة
  },
  {
    title_en: "Integrity",
    title_ar: "النزاهة",
    des_en:
      "We operate with transparency, honesty, and accountability in all our relationships.",
    des_ar: "نعمل بشفافية وصدق ومسؤولية في جميع علاقاتنا.",
    icon: ShieldCheck, // نزاهة
  },
  {
    title_en: "Innovation",
    title_ar: "الابتكار",
    des_en:
      "We embrace continuous improvement and new technologies to enhance our capabilities and solutions.",
    des_ar: "نتبنى التطوير المستمر والتقنيات الحديثة لتعزيز قدراتنا وحلولنا.",
    icon: Lightbulb, // ابتكار
  },
  {
    title_en: "Customer Focus",
    title_ar: "التركيز على العميل",
    des_en:
      "We build long-term partnerships by understanding our customers’ needs and delivering measurable value.",
    des_ar:
      "نبني شراكات طويلة الأمد من خلال فهم احتياجات عملائنا وتقديم قيمة حقيقية.",
    icon: Users, // العملاء
  },
  {
    title_en: "Sustainability",
    title_ar: "الاستدامة",
    des_en:
      "We aim to operate responsibly, optimizing processes and reducing environmental impact wherever possible.",
    des_ar:
      "نسعى للعمل بمسؤولية من خلال تحسين العمليات وتقليل التأثير البيئي قدر الإمكان.",
    icon: Leaf, // استدامة
  },
  {
    title_en: "Teamwork",
    title_ar: "العمل الجماعي",
    des_en:
      "We believe our strength comes from collaboration, shared goals, and a commitment to collective success.",
    des_ar:
      "نؤمن أن قوتنا تأتي من التعاون والأهداف المشتركة والالتزام بالنجاح الجماعي.",
    icon: UserRoundCheck, // فريق العمل
  },
];

// data/products.ts
export const products = [
  {
    id: 1,
    image: "/products/1.png",
    icon: "profiles",
    title_en: "Profiles",
    title_ar: "قطاعات الألومنيوم",
    desc_en:
      "High-performance aluminum profiles designed for structural strength and long-term durability.",
    desc_ar:
      "قطاعات ألومنيوم عالية الأداء مصممة لتوفير قوة هيكلية ومتانة طويلة الأمد.",
    cta_en: "View Specifications",
    cta_ar: "عرض المواصفات",
  },
  {
    id: 2,
    image: "/products/2.jpg",
    icon: "facades",
    title_en: "Facades",
    title_ar: "الواجهات",
    desc_en:
      "Advanced curtain wall and facade systems combining aesthetics with energy efficiency.",
    desc_ar: "أنظمة واجهات متطورة تجمع بين الجمال المعماري وكفاءة الطاقة.",
    cta_en: "Explore Designs",
    cta_ar: "استعرض التصاميم",
  },
  {
    id: 3,
    image: "/products/3.jpg",
    icon: "accessories",
    title_en: "Accessories",
    title_ar: "الإكسسوارات",
    desc_en:
      "Precision-engineered hardware ensuring smooth operation and system longevity.",
    desc_ar: "إكسسوارات هندسية دقيقة تضمن أداءً سلسًا وعمرًا أطول للأنظمة.",
    cta_en: "Browse Catalog",
    cta_ar: "تصفح الكتالوج",
  },
];
